/*
 * youtube-facade.js -- click-to-play přepínač YouTube facade (08-11).
 *
 * CO DĚLÁ. Klik na `.yt-facade-play` uvnitř `.yt-facade[data-video-id]`
 * nahradí celý náhled `<iframe>` na `youtube-nocookie.com/embed/{id}`
 * s `autoplay=1` -- divák už kliknul jednou, druhý klik do přehrávače by
 * byl práce navíc.
 *
 * PROČ MODUL A NE INLINE `<script>`. Stará šablona tiskla přepínač jako
 * inline blok přímo v šablonové části a hlídala konstantou, aby ho na
 * stránce nebylo víc. Nová šablona má inline skripty omezené allowlistem
 * s rozpočtem (kontrakt A4, gate `scripts/lint-theme-2026.sh`) a jediný
 * povolený namespace `TH2026` (A7) -- přepínač proto patří do bundlu.
 * Bonus: v bundlu je jednou bez ohledu na počet videí a nepotřebuje
 * strážní konstantu vůbec.
 *
 * DELEGOVANÝ POSLUCHAČ NA `document`. Facade může do stránky přibýt
 * kdykoli (moduly dispatcheru, budoucí AJAX výpisy), takže se neváže na
 * konkrétní prvky, ale na dokument. Jeden posluchač místo N.
 *
 * BEZ SKRIPTU JE OVLÁDACÍ PRVEK PLNOHODNOTNÝ ODKAZ na youtube.com/watch
 * (kontrakt B). Skript mu klik teprve přebírá -- a jen tehdy, když má
 * všechno, co potřebuje. Když `data-video-id` chybí nebo neodpovídá tvaru
 * YouTube ID, `preventDefault()` se NEZAVOLÁ a prohlížeč odkaz otevře
 * normálně. Rozbitá data tak berou vylepšení, ne přístup k videu.
 *
 * MODIFIKOVANÝ KLIK SE NEPŘEBÍRÁ. Ctrl/Cmd/Shift/prostřední tlačítko
 * znamená „otevři jinde" -- to je vlastnost odkazu a skript ji nesmí
 * vzít (týž důvod jako u navigace, 07-03).
 */

/* Táž kontrola tvaru jako v šabloně -- ID jde do `src` cizí domény. */
const ID_RX = /^[A-Za-z0-9_-]{11}$/;

function isPlainClick(event) {
	return event.button === 0
		&& !event.metaKey
		&& !event.ctrlKey
		&& !event.shiftKey
		&& !event.altKey;
}

function onClick(event) {
	/*
	 * NALEZ 1.27.13 (triaz dvojiteho tapu, typ 5). Puvodne poslouchal jen
	 * `.yt-facade-play` -- cela plocha fasady ma `cursor: pointer`
	 * (vypada klikatelne), ale tap mimo 44x44 tlacitko uprostred nedelal
	 * nic. Posluchac ted najde cely `.yt-facade` obal; tlacitko uvnitr
	 * zustava jako skutecny `<a>` pro klavesnici a ctecky (viz nize).
	 */
	if (!event.target.closest) {
		return;
	}

	/*
	 * `.dvideo` (detail hry) je 10px paddingovy ramecek kolem fasady --
	 * cursor:pointer a hover-scale (`components.css:11020`) delaji
	 * klikatelny cely ramecek, ne jen fasadu uvnitr. Tap do paddingu bez
	 * teto vetve by dal typ 5 (vypada klikatelne, neni).
	 */
	const facade = event.target.closest('.yt-facade')
		|| (event.target.closest('.dvideo') && event.target.closest('.dvideo').querySelector('.yt-facade'));

	if (!facade || !isPlainClick(event)) {
		return;
	}

	const play = facade.querySelector('.yt-facade-play');

	if (!play) {
		return;
	}

	const id = facade.getAttribute('data-video-id') || '';

	if (!ID_RX.test(id)) {
		return; // Odkaz zůstává odkazem -- viz hlavička.
	}

	event.preventDefault();

	const frame = document.createElement('iframe');

	frame.className = 'yt-facade-iframe';
	frame.setAttribute('src', `https://www.youtube-nocookie.com/embed/${id}?autoplay=1`);
	frame.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
	frame.setAttribute('allowfullscreen', '');
	frame.setAttribute('frameborder', '0');

	/*
	 * Rámec potřebuje jméno (W8) -- bere se z přístupného jména odkazu,
	 * které už je složené z popisku z registru a titulku videa.
	 */
	const label = play.getAttribute('aria-label');

	if (label) {
		frame.setAttribute('title', label);
	}

	/*
	 * WRAPPER `.yt-facade` ZŮSTÁVÁ V DOM (08-16b, B7). Do 08-16b se celý
	 * nahrazoval `<iframe>`em -- jenže poměr stran drží právě wrapper
	 * (dvojvrstvě: `::before { padding-top: 56.25% }` plus
	 * `@supports (aspect-ratio: 1)`). Rámec si po náhradě musel poměr
	 * nést sám přes bezpodmínečné `aspect-ratio`, které Safari 14.1
	 * z matice podpory nezná -- video tam mělo nulovou výšku.
	 *
	 * Vyprázdní se tedy jen VNITŘEK facade (náhled + terč) a rámec se
	 * vloží dovnitř; třída `--playing` je pro případné CSS rozlišení.
	 */
	while (facade.firstChild) {
		facade.removeChild(facade.firstChild);
	}

	facade.classList.add('yt-facade--playing');
	facade.appendChild(frame);

	/*
	 * Fokus jde na vložený rámec: divák kliknul na prvek, který právě
	 * zmizel z obalu, a bez tohohle řádku by se fokus vrátil na `<body>`
	 * (klávesnicová navigace by začínala od začátku stránky).
	 */
	if (typeof frame.focus === 'function') {
		frame.focus();
	}
}

export function init() {
	if (!document.querySelector('.yt-facade')) {
		return;
	}

	document.addEventListener('click', onClick);
}
